import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Search,
  Users,
  X,
  ChevronLeft,
  ChevronRight,
  Send,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  HelpCircle,
  Pencil,
  RotateCcw,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SequenceEmailComposer } from '@/components/email/SequenceEmailComposer';
import { SafetyValidationPanel } from './SafetyValidationPanel';
import { MailingAddressRequiredDialog } from '@/components/settings/MailingAddressRequiredDialog';
import { campaignWizardAtom } from '@/lib/atoms';
import { cn } from '@/lib/utils';
import { toRpcFilters } from '@/lib/filters/rpc';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { SendTestEmailDialog } from '../SendTestEmailDialog';
import { useToast } from '@/hooks/use-toast';
import { useReconnectErrorToast } from '@/lib/email/useReconnectErrorToast';
import { useEmulationGuard } from '@/hooks/useEmulationGuard';
import { useCreateHermesCampaign, useActivateHermesCampaign } from '@/hooks/useHermesCampaigns';
import { useFindOrCreateHermesAccount, useHermesEmailAccounts } from '@/hooks/useHermesEmailAccounts';
import { useEmailSender } from '@/hooks/useEmailSender';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { toError } from '@/lib/error-handling';
import { renderCampaignTemplate } from '@/lib/email/campaign-merge';
import type { Contact } from '@/types/contact';
import type { CreateHermesCampaignRequest } from '@/types/hermes-campaign';

type WizardStep = 1 | 2 | 3 | 4;
type SchedulingMode = 'after_existing' | 'prioritize' | 'balanced';

// The per-account safety floor (minutes between sends). The slider can only
// make a campaign send slower than this; the queue processor enforces it too.
// Lowered from 15 → 5 on 2026-06-01 (product decision) — same value lives in
// supabase/functions/email-queue-processor (MIN_ACCOUNT_INTERVAL_MINUTES) and
// supabase/functions/campaign-scheduler. Keep them in sync.
const MIN_INTERVAL_MINUTES = 5;
// Recommended default when the wizard opens — the "safe zone" preset.
const DEFAULT_INTERVAL_MINUTES = 15;
const MAX_INTERVAL_MINUTES = 120;
// Quick-pick presets surfaced above the slider.
const INTERVAL_PRESETS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 5, label: '5 min' },
  { value: 15, label: '15 min' },
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hour' },
];
// Per-account daily send cap (default). Used only for the client-side estimate.
const DEFAULT_DAILY_QUOTA = 50;
// Above this many auto-resolved recipients, confirm intent before loading them.
const BULK_CONFIRM_THRESHOLD = 500;
// Deliverability gate (mirrors campaign-scheduler's isValidEmail). A blank or
// syntactically-invalid primary email is undeliverable — flag it in the roster
// and exclude it from the send set so the wizard count matches the server.
// Incident 2026-06-07: a campaign queued 45 recipients with EMPTY emails;
// Outlook rejected them and the circuit breaker auto-paused the account.
// Conservative: only blank / no-'@' / no-domain addresses are rejected.
function hasValidEmail(email: string | null | undefined): boolean {
  if (typeof email !== 'string') return false;
  const trimmed = email.trim();
  if (trimmed.length === 0) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
}
// Mirrors buildComplianceFooter in email-queue-processor. Shown in the Review
// preview so users see what recipients actually receive — the physical mailing
// address (with proper line breaks) sits above the unsubscribe link inside one
// bordered block. The link href is a placeholder; the real per-recipient
// unsubscribe URL is generated at send time.
function buildComplianceFooterPreviewHtml(address: string): string {
  const addressLines = address
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .join('<br/>');
  const addressBlock = addressLines
    ? `<p style="margin:0 0 8px 0;">${addressLines}</p>`
    : '';
  return (
    `<div style="margin-top:24px;padding-top:16px;border-top:1px solid #e5e7eb;font-size:12px;color:#9ca3af;text-align:center;line-height:1.6;">` +
    addressBlock +
    `<p style="margin:0;">If you no longer wish to receive these emails, you can ` +
    `<a href="#" style="color:#6b7280;text-decoration:underline;">unsubscribe</a>.</p>` +
    `</div>`
  );
}

export function CampaignWizardDialog() {
  const [wizardState, setWizardState] = useAtom(campaignWizardAtom);
  const navigate = useNavigate();
  const { currentWorkspace } = useWorkspaces();
  const { toast } = useToast();
  const showReconnectErrorToast = useReconnectErrorToast();
  const { isEmulating, blockIfEmulating } = useEmulationGuard();
  const createCampaignMutation = useCreateHermesCampaign();
  const activateCampaignMutation = useActivateHermesCampaign();
  const findOrCreateHermesAccount = useFindOrCreateHermesAccount();
  const { data: hermesAccounts } = useHermesEmailAccounts();
  const { providers: emailProviders } = useEmailSender();

  // Wizard state
  const [step, setStep] = useState<WizardStep>(1);
  // `selectedContacts` is the display roster — everyone in the filter/selection,
  // including contacts not opted in to marketing (shown flagged in Step 1).
  const [selectedContacts, setSelectedContacts] = useState<Contact[]>([]);
  // The opted-in subset is the ACTUAL send set: only these are counted, safety-
  // validated, previewed, and written as recipients. Non-opted contacts stay
  // visible in the roster but are never emailed (campaign-scheduler also drops
  // them server-side). See #447, #450. A contact must ALSO have a deliverable
  // primary email — a blank/invalid address is flagged in the roster and
  // excluded here so the count matches what campaign-scheduler will actually
  // queue (it drops invalid-email recipients too). See incident 2026-06-07.
  const optedInContacts = useMemo(
    () =>
      selectedContacts.filter(
        (c) => c.remarket === 'YES' && hasValidEmail(c.primary_email),
      ),
    [selectedContacts],
  );
  const [campaignName, setCampaignName] = useState('');

  // True while resolveBulkSelection()/confirmBulkAdd() fetch the full recipient
  // set from the server. A filter can take 5–30s to resolve (chunked
  // get_filtered_contact_ids + contact fetch); without this the roster sits on
  // the "No recipients selected" empty state the whole time and looks broken.
  const [isResolving, setIsResolving] = useState(false);

  // Step 1 — search
  const [searchValue, setSearchValue] = useState('');

  // Step 2 — compose
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSenderId, setComposeSenderId] = useState('');
  const [showTestDialog, setShowTestDialog] = useState(false);

  // Resolve the selected sender's real per-account daily send cap so the
  // schedule estimate reflects this account, not the 50-default. composeSenderId
  // is an email_providers.id; match it to a hermes account by email_address
  // (the email_providers email === hermes_email_accounts.email_address).
  const senderDailyLimit = useMemo(() => {
    const selectedEmail = emailProviders
      .find((p) => p.id === composeSenderId)
      ?.email?.toLowerCase();
    const matchedAccount = selectedEmail
      ? hermesAccounts?.find(
          (a) => a.email_address.toLowerCase() === selectedEmail,
        )
      : undefined;
    return matchedAccount?.daily_email_limit ?? DEFAULT_DAILY_QUOTA;
  }, [emailProviders, hermesAccounts, composeSenderId]);
  const [safetyValidationPassed, setSafetyValidationPassed] = useState(false);

  // Step 3 — per-recipient overrides (inline edit in the Review step). Keyed by
  // contact.id (never email — two contacts can share an email). The value is the
  // already-merged final subject/body the user hand-edited; it's threaded into
  // buildCampaignData and the scheduler stores it verbatim on that recipient's
  // queue job. `overrideBaseline` records the template the edits were made
  // against, so a later template change can be detected and the user warned
  // before the edits are discarded.
  const [overrides, setOverrides] = useState<Map<string, { subject: string; body: string }>>(new Map());
  const [overrideBaseline, setOverrideBaseline] = useState<{ subject: string; body: string } | null>(null);
  const [templateChangeConfirm, setTemplateChangeConfirm] = useState(false);
  // Mirror of `overrides` for synchronous reads. buildCampaignData runs in the
  // same tick as the discard-confirm handler (which clears overrides then
  // immediately saves), so reading React state there would see the pre-clear
  // value. Every mutation updates this ref synchronously; buildCampaignData
  // reads the ref, render paths read state.
  const overridesRef = useRef(overrides);
  const setOverridesSynced = (next: Map<string, { subject: string; body: string }>) => {
    overridesRef.current = next;
    setOverrides(next);
  };
  // The action to run once the user confirms discarding stale per-recipient
  // edits (go to Review, or save draft). Held in a ref, not state, so it never
  // triggers a re-render. See feedback_function_in_state_render_loop.
  const pendingAfterDiscardRef = useRef<(() => void) | null>(null);

  // Step 4 — schedule
  const [schedulingMode, setSchedulingMode] = useState<SchedulingMode>('after_existing');
  const [sendIntervalMinutes, setSendIntervalMinutes] = useState(DEFAULT_INTERVAL_MINUTES);
  const [existingPendingCount, setExistingPendingCount] = useState(0);
  const [isScheduling, setIsScheduling] = useState(false);
  // Synchronous in-flight guard for create/send. `isScheduling` can't stop a double-click:
  // the button re-renders disabled only after the handler's first await (incident: two
  // drafts 0.45 s apart). Taken at the top of each handler, released in `finally`.
  const inFlightRef = useRef(false);
  // One wizard session yields at most one draft. Holds the idempotency key, the content it
  // was built from, and the draft id once created, so a retry after a failed activation
  // reuses the draft, while an edit after a failure replaces it (`staleId` is deleted only
  // once the newer draft exists, so the newest content always survives).
  const draftRef = useRef<{ fingerprint: string; key: string; campaignId?: string; staleId?: string } | null>(null);

  // Confirmation gate for large auto-resolved recipient sets.
  const [bulkConfirm, setBulkConfirm] = useState<{ ids: string[]; count: number } | null>(null);

  // Inline CAN-SPAM mailing-address gate. When the workspace has no compliance
  // address and no per-sender override, activating the campaign would either
  // trip the client-side check below OR be rejected by the
  // hermes_require_physical_address_on_activate DB trigger. Both paths now
  // surface this dialog (instead of a toast that lost the wizard context),
  // and the user-correctable error is NOT sent to GlitchTip.
  const [showMailingAddressDialog, setShowMailingAddressDialog] = useState(false);

  // Pre-flight suppression warning: when the user clicks "Schedule Send",
  // we look up email_address rows for the selected recipients and flag any
  // that are bounced / unsubscribed / complained. The user is shown the list
  // and can confirm (we then trim them from selectedContacts so backend counts
  // stay clean — see decision note in the PR description) or cancel.
  interface SuppressedRecipient {
    contactId: string;
    email: string;
    name: string;
    status: 'bounced' | 'unsubscribed' | 'complained';
  }
  const [suppressionConfirm, setSuppressionConfirm] = useState<
    | {
        recipients: SuppressedRecipient[];
        totalSelected: number;
      }
    | null
  >(null);

  // Contact search — isolated local query so it does NOT write the shared
  // contactsListAtom (which would otherwise mutate the contacts page behind
  // the modal as the user types). See #438.
  const [searchContacts, setSearchContacts] = useState<Contact[]>([]);
  useEffect(() => {
    if (!searchValue.trim() || !currentWorkspace?.id) {
      setSearchContacts([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.rpc('filter_contacts', {
          p_workspace_id: currentWorkspace.id,
          filters: toRpcFilters({ search: searchValue }),
          page_size: 10,
          page_number: 1,
          count_only: false,
          sort_config: null,
        });
        if (cancelled) return;
        const rows = ((data as any[]) ?? []).map((c) => {
          const { total_count, ...rest } = c;
          return rest as Contact;
        });
        setSearchContacts(rows);
      } catch {
        if (!cancelled) setSearchContacts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [searchValue, currentWorkspace?.id]);

  // Initialize from atom when wizard opens
  useEffect(() => {
    if (wizardState.open) {
      setSelectedContacts(wizardState.selectedContacts || []);
      setStep(1);
      setCampaignName('');
      setComposeSubject('');
      setComposeBody('');
      setComposeSenderId('');
      setSafetyValidationPassed(false);
      setOverridesSynced(new Map());
      setOverrideBaseline(null);
      setTemplateChangeConfirm(false);
      setSearchValue('');
      setSchedulingMode('after_existing');
      setSendIntervalMinutes(DEFAULT_INTERVAL_MINUTES);
      setIsScheduling(false);
      setBulkConfirm(null);
      setIsResolving(false);
      setShowMailingAddressDialog(false);
      setSuppressionConfirm(null);

      // Resolve bulk selection if present
      if (wizardState.bulkSelection?.mode === 'all_matching' && currentWorkspace) {
        resolveBulkSelection();
      }
    } else {
      // A draft left by a failed send is deliberately KEPT on close: it may hold the only
      // copy of the user's content. Just forget it so the next session starts fresh.
      draftRef.current = null;
    }
  }, [wizardState.open]);

  useEffect(() => {
    if (wizardState.open && currentWorkspace) {
      checkExistingJobs();
    }
  }, [wizardState.open, currentWorkspace?.id]);

  const fetchContactsByIds = async (ids: string[]): Promise<Contact[]> => {
    const all: Contact[] = [];
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      // Read from the VIEW, not the base `contacts` table: primary_email /
      // primary_phone are joined columns that only exist on
      // contacts_with_primary_details. Selecting them from `contacts` throws
      // "column primary_email does not exist" — which was previously swallowed
      // (error wasn't destructured), yielding an empty roster and a misleading
      // "0 recipients added". Surface the error so a real failure isn't silent.
      const { data, error } = await supabase
        .from('contacts_with_primary_details')
        .select('id, first_name, last_name, primary_email, company_name, job_title, primary_phone, remarket')
        .in('id', chunk);
      if (error) throw error;
      if (data) all.push(...(data as Contact[]));
    }
    return all;
  };

  // Load the resolved contacts as the recipient roster. We intentionally do NOT
  // drop contacts that aren't opted in to marketing here — dropping them
  // silently collapsed the list (often to ~1) with no explanation. Instead the
  // full set is shown in Step 1 (non-opted rows flagged), while the opted-in
  // subset (`optedInContacts`) is what actually gets emailed. The server
  // (campaign-scheduler) re-enforces the opt-in gate. See #447, #450.
  const applyResolvedRecipients = (fetched: Contact[]) => {
    setSelectedContacts(fetched);
    const notOpted = fetched.filter((c) => c.remarket !== 'YES').length;
    const opted = fetched.length - notOpted;
    toast({
      description:
        notOpted > 0
          ? `${opted} recipient${opted !== 1 ? 's' : ''} added · ${notOpted} not opted in to marketing (won't be emailed).`
          : `${opted} recipient${opted !== 1 ? 's' : ''} added.`,
    });
  };

  const resolveBulkSelection = async () => {
    const bulkSelection = wizardState.bulkSelection;
    if (!bulkSelection || !currentWorkspace) return;

    setIsResolving(true);
    try {
      // Chunked ID resolution (mirrors BulkEditDialog) so large filtered sets
      // are NOT truncated by a single page limit.
      const allIds: string[] = [];
      let offset = 0;
      const chunkSize = 1000;
      let hasMore = true;
      while (hasMore) {
        const { data: chunk, error } = await supabase
          .rpc('get_filtered_contact_ids', {
            // Translate the raw client filter shape to the RPC shape the SQL
            // expects (snake_case keys, end_value/match_year, etc.) — the same
            // transform useContacts applies. Passing the raw shape silently
            // mis-resolved some conditions (e.g. date BETWEEN), so the resolved
            // set could diverge from the visible filtered list.
            p_workspace_id: currentWorkspace.id,
            p_filters: bulkSelection.filterState
              ? toRpcFilters(bulkSelection.filterState)
              : null,
          })
          .range(offset, offset + chunkSize - 1);
        if (error) throw error;
        const ids = (chunk ?? []).map((row: { contact_id: string }) => row.contact_id);
        allIds.push(...ids);
        hasMore = ids.length === chunkSize;
        offset += chunkSize;
      }

      const resolvedIds = allIds.filter((id: string) => !bulkSelection.excludedIds.has(id));
      if (resolvedIds.length === 0) return;

      // Large sets: confirm intent before loading them as recipients.
      if (resolvedIds.length > BULK_CONFIRM_THRESHOLD) {
        setBulkConfirm({ ids: resolvedIds, count: resolvedIds.length });
        return;
      }
      applyResolvedRecipients(await fetchContactsByIds(resolvedIds));
    } catch (err) {
      console.error('[CampaignWizard] Failed to resolve bulk selection:', err);
      captureError(err as Error, { feature: 'campaign-wizard-resolve-bulk' });
    } finally {
      // The large-set path returns early after opening the confirm dialog —
      // resetting here hands the "loading" cue over to that dialog rather than
      // leaving a spinner stuck behind it.
      setIsResolving(false);
    }
  };

  const confirmBulkAdd = async () => {
    if (!bulkConfirm) return;
    setIsResolving(true);
    try {
      applyResolvedRecipients(await fetchContactsByIds(bulkConfirm.ids));
    } catch (err) {
      console.error('[CampaignWizard] Failed to load bulk recipients:', err);
      captureError(err as Error, { feature: 'campaign-wizard-bulk-add' });
    } finally {
      setBulkConfirm(null);
      setIsResolving(false);
    }
  };

  const checkExistingJobs = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    try {
      const { count, error } = await supabase
        .from('hermes_email_queue_jobs')
        .select('id', { count: 'exact', head: true })
        .eq('workspace_id', currentWorkspace.id)
        .eq('status', 'pending');
      if (!error && count !== null) {
        setExistingPendingCount(count);
      }
    } catch {
      // Non-critical
    }
  }, [currentWorkspace?.id]);

  // Best-effort delete of a superseded draft THIS session created (FKs cascade to its queue
  // jobs and recipients). The status filter makes it a no-op once a draft is activated.
  const discardDraft = async (campaignId: string) => {
    const { error } = await supabase
      .from('hermes_email_campaigns')
      .delete()
      .eq('id', campaignId)
      .eq('status', 'draft');
    if (error) captureError(toError(error), { feature: 'campaign-wizard-discard-draft' });
  };

  // Create this session's draft once; after it exists, drop the draft it superseded.
  const ensureDraft = async (
    draft: NonNullable<typeof draftRef.current>,
    campaignData: Parameters<typeof createCampaignMutation.mutateAsync>[0],
  ) => {
    if (!draft.campaignId) {
      draft.campaignId = (await createCampaignMutation.mutateAsync(campaignData)).id;
      if (draft.staleId) await discardDraft(draft.staleId);
      draft.staleId = undefined;
    }
    return draft.campaignId;
  };

  // Serialize create/send work behind inFlightRef (see its declaration).
  const runExclusive = async (work: () => Promise<void>) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setIsScheduling(true);
    try {
      await work();
    } finally {
      inFlightRef.current = false;
      setIsScheduling(false);
    }
  };

  const handleClose = () => {
    setWizardState({ open: false, selectedContacts: [] });
  };

  // Step 1 handlers
  const handleAddContact = (contact: Contact) => {
    // Add the contact to the roster regardless of opt-in status. Non-opted
    // contacts are shown flagged ("Not opted in") and excluded from the actual
    // send (only `optedInContacts` are emailed; the server re-enforces). This
    // is clearer than silently refusing to add them. See #447.
    if (!selectedContacts.find(c => c.id === contact.id)) {
      setSelectedContacts(prev => [...prev, contact]);
    }
    setSearchValue('');
  };

  const handleRemoveContact = (contactId: string) => {
    setSelectedContacts(prev => prev.filter(c => c.id !== contactId));
  };

  // Build the create request. The campaign + queue jobs are created exactly
  // once — at "Schedule Send" or "Save as Draft" — always as a DRAFT, so the
  // interval/mode chosen on the Schedule step are applied at job-creation time.
  // A draft never sends (the processor only claims active campaigns), so this
  // is a safe checkpoint; "Schedule Send" flips it to active as a separate step.
  //
  // `recipientsOverride` lets callers explicitly supply the recipient set
  // (used by the suppression-confirm path, where the trimmed set may not
  // have committed to `selectedContacts` state yet when buildCampaignData
  // is called). When omitted, falls back to the derived `optedInContacts`.
  const buildCampaignData = async (
    recipientsOverride?: Contact[],
  ): Promise<(CreateHermesCampaignRequest & { recipients: any[] }) | null> => {
    if (!currentWorkspace) return null;
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const emailAccountId = await findOrCreateHermesAccount.mutateAsync(composeSenderId);
    const name = campaignName || `${composeSubject} - ${format(new Date(), 'MM/dd/yy')}`;
    const recipients = recipientsOverride ?? optedInContacts;

    // Defense in depth: the Step-2 → Review guard normally discards per-recipient
    // edits when the base template changes, so we should never reach a send/draft
    // build with overrides that are stale vs the current template. If we somehow
    // do (e.g. a future step-jump path), drop the overrides rather than ship old
    // per-recipient text under a new template, and log it. The happy path is
    // never stale, so effectiveOverrides === overridesRef.current there.
    const baselineStale =
      !!overrideBaseline &&
      (composeSubject !== overrideBaseline.subject || composeBody !== overrideBaseline.body);
    if (baselineStale && overridesRef.current.size > 0) {
      captureError(
        new Error('Stale per-recipient overrides dropped at campaign build (template changed after editing)'),
        { feature: 'campaign-wizard-stale-overrides' },
      );
    }
    const effectiveOverrides = baselineStale
      ? new Map<string, { subject: string; body: string }>()
      : overridesRef.current;

    const payload = {
      workspace_id: currentWorkspace.id,
      user_id: user.id,
      email_account_id: emailAccountId,
      name,
      subject_template: composeSubject,
      body_template: composeBody,
      recipient_count: recipients.length,
      status: 'draft' as const,
      scheduling_mode: schedulingMode,
      send_interval_minutes: sendIntervalMinutes,
      // Only opted-in contacts are written as recipients — non-opted contacts
      // in the roster are display-only. campaign-scheduler re-enforces this.
      recipients: recipients.map(contact => {
        const override = effectiveOverrides.get(contact.id);
        return {
          contact_id: contact.id,
          email: contact.primary_email || '',
          first_name: contact.first_name,
          last_name: contact.last_name,
          company: contact.company_name,
          occupation: contact.job_title || '',
          phone: contact.primary_phone || '',
          // Inline per-recipient edit from the Review step (if any). The
          // scheduler stores these verbatim on the recipient's job instead of
          // rendering the template.
          ...(override
            ? {
                rendered_subject_override: override.subject,
                rendered_body_override: override.body,
              }
            : {}),
        };
      }),
    };

    // Same content as the previous attempt → same key (and draft). Changed content → mint a
    // new key so the server can't replay the stale draft; ensureDraft deletes the stale one
    // after the replacement is created.
    const fingerprint = JSON.stringify(payload);
    const prev = draftRef.current;
    const draft =
      prev?.fingerprint === fingerprint
        ? prev
        : {
            fingerprint,
            key: `${user.id}-${Date.now()}-${Math.random()}`,
            staleId: prev?.campaignId ?? prev?.staleId,
          };
    draftRef.current = draft;
    return { ...payload, idempotency_key: draft.key };
  };

  // Persist an inline per-recipient edit from the Review step. Records the
  // template the edit was made against (overrideBaseline) so a subsequent
  // template change can warn-then-discard.
  const handleOverrideSave = (contactId: string, value: { subject: string; body: string }) => {
    const next = new Map(overridesRef.current);
    next.set(contactId, value);
    setOverridesSynced(next);
    setOverrideBaseline({ subject: composeSubject, body: composeBody });
  };

  // Revert a single recipient back to the campaign template.
  const handleOverrideReset = (contactId: string) => {
    const next = new Map(overridesRef.current);
    next.delete(contactId);
    setOverridesSynced(next);
  };

  // Guard any action that consumes overrides (advance to Review, save draft).
  // If the user customized individual emails and then changed the base template,
  // warn before proceeding — otherwise the scheduler would mix old per-recipient
  // text with a new template. On confirm the edits are discarded and the action
  // runs; with no stale edits it runs immediately.
  const guardOverrideConsistency = (proceed: () => void) => {
    const stale =
      overrides.size > 0 &&
      overrideBaseline &&
      (composeSubject !== overrideBaseline.subject || composeBody !== overrideBaseline.body);
    if (stale) {
      pendingAfterDiscardRef.current = proceed;
      setTemplateChangeConfirm(true);
      return;
    }
    proceed();
  };
  const proceedToReview = () => guardOverrideConsistency(() => setStep(3));

  // Pre-flight suppression check. Mirrors the queue-processor's suppression
  // gate at supabase/functions/email-queue-processor/index.ts (the (contact_id,
  // email) join) so a recipient flagged here is exactly the same recipient
  // the backend would reject at send time.
  //
  // We match on the SAME email each recipient would actually receive — i.e.
  // the contact's primary_email, which is what buildCampaignData writes into
  // recipients[].email. A contact may have multiple email_address rows
  // (primary + secondary); a bounce on the secondary must not flag the
  // primary, so we filter `.in('email', primaryEmails)` after the
  // contact_id IN clause.
  const findSuppressedRecipients = async (): Promise<SuppressedRecipient[]> => {
    if (optedInContacts.length === 0) return [];
    const emailable = optedInContacts.filter(
      (c): c is typeof c & { primary_email: string } =>
        typeof c.primary_email === 'string' && c.primary_email.length > 0,
    );
    if (emailable.length === 0) return [];

    const contactIds = emailable.map((c) => c.id);
    const emailsLower = new Set(emailable.map((c) => c.primary_email.toLowerCase()));

    // Chunk the IN clauses — Supabase / PostgREST has a URL-length limit and
    // some workspaces have thousands of recipients in a single campaign.
    const CHUNK = 200;
    const hits: { contact_id: string; email: string; email_status: string }[] = [];
    for (let i = 0; i < contactIds.length; i += CHUNK) {
      const chunk = contactIds.slice(i, i + CHUNK);
      const { data, error } = await supabase
        .from('email_address')
        .select('contact_id, email, email_status')
        .in('contact_id', chunk)
        .in('email_status', ['bounced', 'unsubscribed', 'complained']);
      if (error) throw toError(error);
      if (data) hits.push(...(data as { contact_id: string; email: string; email_status: string }[]));
    }

    // Filter to only the primary email per contact (case-insensitive). Drop
    // bounces on alternate addresses — those aren't what we'd send to.
    const byContact = new Map(emailable.map((c) => [c.id, c]));
    const suppressed: SuppressedRecipient[] = [];
    for (const row of hits) {
      if (!emailsLower.has(row.email.toLowerCase())) continue;
      const contact = byContact.get(row.contact_id);
      if (!contact) continue;
      // Only match the row whose email equals the contact's primary_email.
      if ((contact.primary_email || '').toLowerCase() !== row.email.toLowerCase()) continue;
      const status = row.email_status as 'bounced' | 'unsubscribed' | 'complained';
      if (!['bounced', 'unsubscribed', 'complained'].includes(status)) continue;
      const name = `${contact.first_name ?? ''} ${contact.last_name ?? ''}`.trim() || contact.primary_email;
      suppressed.push({
        contactId: contact.id,
        email: contact.primary_email,
        name,
        status,
      });
    }
    return suppressed;
  };

  // CAN-SPAM: resolve whether a physical mailing address exists for the chosen
  // sender, mirroring the server activation gate's order (per-sender address
  // first, then workspace fallback — see the
  // hermes_require_physical_address_on_activate trigger). composeSenderId is the
  // email_providers.id (useFindOrCreateHermesAccount resolves it the same way).
  const senderHasMailingAddress = async (): Promise<boolean> => {
    if (!currentWorkspace) return false;
    const { data: prov } = await supabase
      .from('email_providers')
      .select('physical_address')
      .eq('id', composeSenderId)
      .maybeSingle();
    if ((prov?.physical_address || '').trim()) return true;
    const { data: ws } = await supabase
      .from('workspace_settings')
      .select('compliance_physical_address')
      .eq('workspace_id', currentWorkspace.id)
      .maybeSingle();
    return !!(ws?.compliance_physical_address || '').trim();
  };

  // Step 4: create the draft (with the chosen interval/mode), then activate it
  // via the existing, proven activation path, then go to the summary.
  //
  // The actual send work is in `executeScheduleSend` so the suppression
  // pre-flight dialog can call it after the user confirms (without re-running
  // the address + suppression checks). `recipientsOverride` is forwarded to
  // buildCampaignData so the trimmed set is used even when React state for
  // selectedContacts hasn't flushed yet.
  // Callers hold runExclusive.
  const executeScheduleSend = async (recipientsOverride?: Contact[]) => {
    try {
      const campaignData = await buildCampaignData(recipientsOverride);
      const draft = draftRef.current;
      if (!campaignData || !draft) return;
      // A retry after a failed activation reuses this session's draft instead of creating another.
      const campaignId = await ensureDraft(draft, campaignData);
      await activateCampaignMutation.mutateAsync(campaignId);
      draftRef.current = null; // activated: nothing to discard on close
      handleClose();
      navigate(`/inbox/campaigns/${campaignId}/summary`);
    } catch (error) {
      console.error('Error scheduling campaign:', error);
      // Sender's OAuth token can't be refreshed → actionable reconnect toast
      // (emulation-aware). `finally` still clears the scheduling spinner.
      const senderType = emailProviders.find((p) => p.id === composeSenderId)?.type;
      if (showReconnectErrorToast(error, senderType)) {
        return;
      }
      // edge-functions-api.invokeFunction parses the FunctionsHttpError body and
      // surfaces the real error_code (e.g. 'no_opted_in_recipients'), which
      // useCreateHermesCampaign attaches to the thrown Error. Pass it to
      // GlitchTip so failures group by code, and use it to render an actionable
      // toast instead of the generic edge-function wrapper text.
      const errorCode = (error as { error_code?: string })?.error_code;
      const rawMessage = error instanceof Error ? error.message : 'Failed to schedule campaign';
      // Detect the DB activation trigger's missing-mailing-address rejection.
      // The trigger raises with ERRCODE = 'check_violation' (Postgres 23514)
      // and a message starting with "A physical mailing address is required".
      // This is reachable when the client-side check above passes but the
      // address is removed between check and activate, or when a per-sender
      // address resolution differs from what the trigger sees. Treat it as
      // user-correctable (no GlitchTip noise, no destructive toast).
      const pgCode = (error as { code?: string })?.code;
      const isMailingAddressGate =
        pgCode === '23514' &&
        typeof rawMessage === 'string' &&
        rawMessage.toLowerCase().includes('physical mailing address');
      if (isMailingAddressGate) {
        console.warn('[CampaignWizard] DB activation gate: mailing address required');
        setIsScheduling(false);
        setShowMailingAddressDialog(true);
        return;
      }
      captureError(error as Error, { feature: 'campaign-wizard-schedule-send', error_code: errorCode });
      let description = rawMessage;
      let title = 'Error';
      if (errorCode === 'no_opted_in_recipients') {
        title = 'No opted-in recipients';
        description = 'None of your selected contacts are opted in to marketing (remarket = YES). Update their consent first.';
      }
      toast({ title, description, variant: 'destructive' });
    }
  };

  // Wizard entry-point for "Schedule Send". Runs pre-flight checks (sender
  // selected, mailing address present, suppressed recipients), then either
  // surfaces a confirmation/correction dialog or calls executeScheduleSend.
  const handleScheduleSend = () => runExclusive(async () => {
    if (blockIfEmulating('Activating an email campaign')) return;
    if (!composeSenderId) {
      toast({ title: 'Sender Required', description: 'Select a sender account first.', variant: 'destructive' });
      return;
    }
    // Block early (before creating the draft) when no mailing address resolves.
    // The DB trigger (hermes_require_physical_address_on_activate) is the
    // authoritative gate; this is the friendly client-side mirror. Instead of
    // a destructive toast that sent the user away to Settings (losing wizard
    // state), surface an inline dialog that lets them enter the workspace
    // address and continue. See MailingAddressRequiredDialog.
    if (!(await senderHasMailingAddress())) {
      setShowMailingAddressDialog(true);
      return;
    }

    // Pre-flight suppression check — informational gate. The backend will
    // also drop suppressed recipients at send time (defense in depth), but
    // surfacing them BEFORE activation lets the user see who won't be reached
    // and decide whether to proceed.
    try {
      const suppressed = await findSuppressedRecipients();
      if (suppressed.length > 0) {
        setSuppressionConfirm({
          recipients: suppressed,
          totalSelected: optedInContacts.length,
        });
        return;
      }
    } catch (err) {
      // Don't block the send if the suppression lookup itself fails — the
      // backend gate is authoritative. Just log and continue.
      console.warn('[CampaignWizard] suppression pre-flight failed; proceeding:', err);
      captureError(err as Error, { feature: 'campaign-wizard-suppression-preflight' });
    }

    await executeScheduleSend();
  });

  // Called after the user enters & saves a workspace mailing address inline.
  // Close the gate dialog and re-run the same activation flow — the second
  // attempt will pass senderHasMailingAddress() and the DB trigger now that
  // workspace_settings.compliance_physical_address is populated. handleScheduleSend
  // then proceeds into the suppression pre-flight as normal.
  const handleMailingAddressSaved = () => {
    setShowMailingAddressDialog(false);
    void handleScheduleSend();
  };

  // Frontend-trim path: when the user clicks "Continue with N recipients" in
  // the suppression dialog, remove the suppressed contacts from
  // selectedContacts AND pass the trimmed set explicitly to
  // executeScheduleSend (so the send doesn't race the React state update).
  // That way the campaign's recipient_count and queue jobs reflect the actual
  // send set, and the post-send UI's "skipped" counter is reserved for
  // genuinely-late bounces (a recipient who unsubscribed AFTER the campaign
  // was scheduled but BEFORE their slot ran). See PR description for the
  // backend-skipping vs frontend-trimming decision rationale.
  const handleConfirmSuppression = () => runExclusive(async () => {
    if (!suppressionConfirm) return;
    const suppressedIds = new Set(suppressionConfirm.recipients.map((r) => r.contactId));
    const trimmed = optedInContacts.filter((c) => !suppressedIds.has(c.id));
    setSelectedContacts((prev) => prev.filter((c) => !suppressedIds.has(c.id)));
    setSuppressionConfirm(null);
    await executeScheduleSend(trimmed);
  });

  const handleCancelSuppression = () => {
    setSuppressionConfirm(null);
  };

  // Save as a draft (no sending) and close. Guarded so a template change made
  // after per-recipient edits warns before discarding them (see proceedToReview).
  const handleSaveDraft = () => guardOverrideConsistency(() => void runExclusive(doSaveDraft));
  const doSaveDraft = async () => {
    if (!composeSenderId) {
      toast({ title: 'Sender Required', description: 'Select a sender account first.', variant: 'destructive' });
      return;
    }
    try {
      const campaignData = await buildCampaignData();
      const draft = draftRef.current;
      if (!campaignData || !draft) return;
      await ensureDraft(draft, campaignData);
      draftRef.current = null; // saved: the next attempt starts a new draft
      toast({ title: 'Draft Saved', description: 'Campaign saved as draft.' });
      handleClose();
    } catch (error) {
      console.error('Error saving draft:', error);
      const errorCode = (error as { error_code?: string })?.error_code;
      captureError(error as Error, { feature: 'campaign-wizard-save-draft', error_code: errorCode });
      const rawMessage = error instanceof Error ? error.message : 'Failed to save draft';
      let description = rawMessage;
      let title = 'Error';
      if (errorCode === 'no_opted_in_recipients') {
        title = 'No opted-in recipients';
        description = 'None of your selected contacts are opted in to marketing (remarket = YES). Update their consent first.';
      }
      toast({ title, description, variant: 'destructive' });
    }
  };

  const handleValidationChange = (isValid: boolean, _warnings: any[]) => {
    setSafetyValidationPassed(isValid);
  };

  // Defense in depth: block progression from Step 2 if the body is empty.
  // TipTap emits '<p></p>' (or '<p><br></p>') for an empty editor, so a bare
  // trim() on the HTML isn't sufficient — strip tags first to check for any
  // actual text. This is the second layer of protection against the empty-
  // body campaign incident (2026-06-01); the primary fix is in
  // SequenceEmailComposer's TemplateSelector onSelect handler.
  const composeBodyHasText = !!composeBody.replace(/<[^>]*>/g, '').trim();
  const composeReady = !!composeSubject.trim() && !!composeSenderId && composeBodyHasText;
  const stepLabels = ['Recipients', 'Compose', 'Review', 'Schedule'];

  return (
    <>
    <Dialog open={wizardState.open} onOpenChange={(open) => { if (!open && !inFlightRef.current) handleClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col p-0 gap-0">
        {/* Header with step indicator */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle className="text-lg">Create Campaign</DialogTitle>
          <div className="flex items-center gap-2 mt-2">
            {stepLabels.map((label, idx) => {
              const stepNum = idx + 1;
              const isActive = step === stepNum;
              const isCompleted = step > stepNum;
              return (
                <div key={label} className="flex items-center gap-2">
                  {idx > 0 && <div className={`h-px w-8 ${isCompleted ? 'bg-primary' : 'bg-border'}`} />}
                  <div className="flex items-center gap-1.5">
                    <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      isActive ? 'bg-primary text-primary-foreground' :
                      isCompleted ? 'bg-primary/20 text-primary' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {isCompleted ? <CheckCircle2 className="h-3.5 w-3.5" /> : stepNum}
                    </div>
                    <span className={`text-sm ${isActive ? 'font-medium' : 'text-muted-foreground'}`}>
                      {label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogHeader>

        {/* Content area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {step === 1 && (
            <StepRecipients
              selectedContacts={selectedContacts}
              isResolving={isResolving}
              expectedCount={wizardState.bulkSelection?.effectiveCount}
              searchValue={searchValue}
              searchContacts={searchContacts || []}
              campaignName={campaignName}
              onSearchChange={setSearchValue}
              onAddContact={handleAddContact}
              onRemoveContact={handleRemoveContact}
              onCampaignNameChange={setCampaignName}
            />
          )}

          {step === 2 && (
            <StepCompose
              composeSubject={composeSubject}
              composeBody={composeBody}
              composeSenderId={composeSenderId}
              safetyValidationPassed={safetyValidationPassed}
              selectedContacts={optedInContacts}
              onSubjectChange={setComposeSubject}
              onBodyChange={setComposeBody}
              onSenderChange={setComposeSenderId}
            />
          )}

          {step === 3 && (
            <StepReviewPreview
              subject={composeSubject}
              body={composeBody}
              contacts={optedInContacts}
              senderId={composeSenderId}
              overrides={overrides}
              onOverrideSave={handleOverrideSave}
              onOverrideReset={handleOverrideReset}
            />
          )}

          {step === 4 && (
            <StepSchedule
              recipientCount={optedInContacts.length}
              sendIntervalMinutes={sendIntervalMinutes}
              onIntervalChange={setSendIntervalMinutes}
              schedulingMode={schedulingMode}
              onSchedulingModeChange={setSchedulingMode}
              existingPendingCount={existingPendingCount}
              safetyValidationPassed={safetyValidationPassed}
              senderDailyLimit={senderDailyLimit}
            />
          )}
        </div>

        {/* Hidden safety validation — runs whenever contacts exist, independent
            of the active step, so the final-send gate is always current. */}
        {currentWorkspace?.id && optedInContacts.length > 0 && (
          <div className="hidden">
            <SafetyValidationPanel
              selectedContacts={optedInContacts}
              workspaceId={currentWorkspace.id}
              onValidationChange={handleValidationChange}
            />
          </div>
        )}

        {/* Footer with navigation */}
        <div className="px-6 py-4 border-t shrink-0 flex items-center justify-between bg-muted/30">
          <div>
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep((step - 1) as WizardStep)}
                disabled={isScheduling}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step === 1 && (
              <Button onClick={() => setStep(2)} disabled={optedInContacts.length === 0}>
                Next: Compose
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
            {step === 2 && (
              <>
                <Button variant="outline" onClick={handleSaveDraft} disabled={!composeReady || isScheduling}>
                  Save as Draft
                </Button>
                <Button onClick={proceedToReview} disabled={!composeReady}>
                  Next: Review
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            )}
            {step === 3 && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setShowTestDialog(true)}
                  disabled={!composeReady}
                >
                  <Send className="h-4 w-4 mr-1" />
                  Send Test Email
                </Button>
                <Button onClick={() => setStep(4)}>
                  Next: Schedule
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </>
            )}
            {step === 4 && (
              <Button
                onClick={handleScheduleSend}
                disabled={isScheduling || !safetyValidationPassed || isEmulating}
                title={isEmulating ? 'Disabled while emulating user' : undefined}
              >
                {isScheduling ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1" />
                    Schedule Send
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {currentWorkspace && (
      <SendTestEmailDialog
        open={showTestDialog}
        onOpenChange={setShowTestDialog}
        subject={composeSubject}
        body={composeBody}
        workspaceId={currentWorkspace.id}
        senderId={composeSenderId}
      />
    )}

    <AlertDialog open={!!bulkConfirm} onOpenChange={(o) => { if (!o) setBulkConfirm(null); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Add up to {bulkConfirm?.count.toLocaleString()} recipients?</AlertDialogTitle>
          <AlertDialogDescription>
            This filter matches up to {bulkConfirm?.count.toLocaleString()} contacts. They'll all be added to the
            list; contacts not opted in to marketing (ReMarket = Yes) are flagged and won't be emailed. Sends are
            paced per the schedule you set, never faster than the per-account limits.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setBulkConfirm(null)}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={confirmBulkAdd}>Add recipients</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {/* Template-change guard. The user customized individual emails in Review,
        then went back and edited the base template. Per-recipient edits are
        the already-merged old text, so we can't safely reconcile them with a
        new template — warn before discarding. */}
    <AlertDialog
      open={templateChangeConfirm}
      onOpenChange={(o) => {
        setTemplateChangeConfirm(o);
        // Drop the pending action if dismissed without choosing (Escape/overlay).
        if (!o) pendingAfterDiscardRef.current = null;
      }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard customized emails?</AlertDialogTitle>
          <AlertDialogDescription>
            You changed the template after customizing {overrides.size} individual{' '}
            email{overrides.size !== 1 ? 's' : ''}. Continuing discards{' '}
            {overrides.size === 1 ? 'that edit' : 'those edits'} and uses the updated
            template for everyone. This can't be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => { pendingAfterDiscardRef.current = null; }}>
            Keep editing template
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              setOverridesSynced(new Map());
              setOverrideBaseline(null);
              setTemplateChangeConfirm(false);
              const proceed = pendingAfterDiscardRef.current;
              pendingAfterDiscardRef.current = null;
              proceed?.();
            }}
          >
            Discard &amp; continue
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {currentWorkspace?.id && (
      <MailingAddressRequiredDialog
        open={showMailingAddressDialog}
        onOpenChange={setShowMailingAddressDialog}
        workspaceId={currentWorkspace.id}
        onSaved={handleMailingAddressSaved}
        onCancel={() => setShowMailingAddressDialog(false)}
      />
    )}

    {/* Suppression pre-flight dialog. Surfaces recipients the backend will
        drop at send time (bounced / unsubscribed / complained) so the user
        sees them BEFORE the campaign activates rather than discovering
        "Failed" badges after the fact. */}
    <AlertDialog
      open={!!suppressionConfirm}
      onOpenChange={(o) => { if (!o) handleCancelSuppression(); }}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Some recipients have unsubscribed</AlertDialogTitle>
          <AlertDialogDescription>
            {suppressionConfirm?.recipients.length} of the {suppressionConfirm?.totalSelected} recipients you selected have previously unsubscribed, bounced, or marked email as spam. They won't receive this campaign.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {suppressionConfirm && (
          <div className="space-y-3">
            <div className="max-h-48 overflow-y-auto rounded-md border bg-muted/30 p-2 text-xs">
              <ul className="space-y-1">
                {suppressionConfirm.recipients.slice(0, 25).map((r) => (
                  <li key={`${r.contactId}-${r.email}`} className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium text-foreground">
                      {r.email}
                      {r.name && r.name !== r.email ? (
                        <span className="text-muted-foreground"> · {r.name}</span>
                      ) : null}
                    </span>
                    <span className="shrink-0 rounded bg-background px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                      {r.status}
                    </span>
                  </li>
                ))}
              </ul>
              {suppressionConfirm.recipients.length > 25 && (
                <p className="mt-2 text-[11px] text-muted-foreground">
                  …and {suppressionConfirm.recipients.length - 25} more.
                </p>
              )}
            </div>
            <p className="text-sm text-muted-foreground">
              You can continue with the remaining {suppressionConfirm.totalSelected - suppressionConfirm.recipients.length} recipient{suppressionConfirm.totalSelected - suppressionConfirm.recipients.length !== 1 ? 's' : ''}, or cancel and update your recipient list.
            </p>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={handleCancelSuppression}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={handleConfirmSuppression}>
            Continue with {(suppressionConfirm?.totalSelected ?? 0) - (suppressionConfirm?.recipients.length ?? 0)} recipient{((suppressionConfirm?.totalSelected ?? 0) - (suppressionConfirm?.recipients.length ?? 0)) !== 1 ? 's' : ''}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

// ─── Step 1: Recipients ──────────────────────────────────────────────

interface StepRecipientsProps {
  selectedContacts: Contact[];
  isResolving: boolean;
  expectedCount?: number;
  searchValue: string;
  searchContacts: Contact[];
  campaignName: string;
  onSearchChange: (v: string) => void;
  onAddContact: (c: Contact) => void;
  onRemoveContact: (id: string) => void;
  onCampaignNameChange: (v: string) => void;
}

function StepRecipients({
  selectedContacts,
  isResolving,
  expectedCount,
  searchValue,
  searchContacts,
  campaignName,
  onSearchChange,
  onAddContact,
  onRemoveContact,
  onCampaignNameChange,
}: StepRecipientsProps) {
  const notOptedCount = selectedContacts.filter((c) => c.remarket !== 'YES').length;
  // Opted-in but with a blank/invalid primary email — also won't be emailed.
  // Mutually exclusive with notOptedCount below (we only flag invalid-email on
  // contacts that ARE opted in, so the two buckets don't double-count).
  const invalidEmailCount = selectedContacts.filter(
    (c) => c.remarket === 'YES' && !hasValidEmail(c.primary_email),
  ).length;
  // Emailable = opted in AND deliverable email. Matches optedInContacts upstream.
  const optedCount = selectedContacts.length - notOptedCount - invalidEmailCount;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Left: campaign name + search */}
      <div className="space-y-4">
        <div>
          <Label htmlFor="wiz-campaign-name" className="text-sm font-medium">
            Campaign Name <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="wiz-campaign-name"
            placeholder="Auto-generated from subject if left blank"
            value={campaignName}
            onChange={(e) => onCampaignNameChange(e.target.value)}
            className="mt-1"
          />
        </div>

        <div className="space-y-2">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Search className="h-4 w-4" />
            Search Contacts
          </h4>
          <div className="relative">
            <Input
              placeholder="Search by name, email, or company..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {searchValue && searchContacts.length > 0 && (
              <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-y-auto">
                {searchContacts.slice(0, 10).map((contact) => (
                  <div
                    key={contact.id}
                    className="p-2.5 hover:bg-muted cursor-pointer border-b last:border-b-0"
                    onClick={() => onAddContact(contact)}
                  >
                    <div className="text-sm font-medium">
                      {contact.first_name} {contact.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {contact.primary_email} {contact.company_name ? `· ${contact.company_name}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right: selected recipients (taller, fills the column) */}
      <div className="space-y-2">
        <h4 className="text-sm font-medium flex items-center gap-2">
          Selected Recipients
          {selectedContacts.length > 0 && (
            <Badge variant="secondary">{selectedContacts.length}</Badge>
          )}
          {isResolving && (
            <span className="inline-flex items-center gap-1 text-xs font-normal text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Loading…
            </span>
          )}
        </h4>
        {/* Summary of who will actually be emailed vs flagged out (not opted in
            and/or blank/invalid email). */}
        {selectedContacts.length > 0 && optedCount > 0 && (notOptedCount > 0 || invalidEmailCount > 0) && (
          <p className="text-xs text-muted-foreground">
            {optedCount} will be emailed
            {notOptedCount > 0 ? ` · ${notOptedCount} not opted in to marketing (won't be emailed)` : ''}
            {invalidEmailCount > 0 ? ` · ${invalidEmailCount} with no valid email (won't be emailed)` : ''}
          </p>
        )}
        {/* Nothing sendable: every contact is unopted and/or has no valid email. */}
        {selectedContacts.length > 0 && optedCount === 0 && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              {invalidEmailCount > 0 && notOptedCount === 0
                ? `None of ${selectedContacts.length === 1 ? 'this contact has' : `these ${selectedContacts.length} contacts have`} a valid email address, so no emails can be sent. Add a valid primary email to email them.`
                : `None of ${selectedContacts.length === 1 ? 'this contact has' : `these ${selectedContacts.length} contacts have`} opted in to marketing (with a valid email), so no emails can be sent. Set Remarket = Yes and a valid email on their contact records to email them.`}
            </span>
          </div>
        )}
        {isResolving && selectedContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center h-[520px] text-muted-foreground border rounded-lg border-dashed">
            <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin opacity-70" />
            <p className="text-sm font-medium">Loading contacts…</p>
            <p className="text-xs">
              {expectedCount
                ? `Fetching ${expectedCount.toLocaleString()} recipient${expectedCount === 1 ? '' : 's'} from your filter`
                : 'Fetching recipients from your filter'}
            </p>
          </div>
        ) : selectedContacts.length > 0 ? (
          <div className="max-h-[520px] overflow-y-auto border rounded-lg divide-y">
            {selectedContacts.map((contact) => {
              const notOpted = contact.remarket !== 'YES';
              // Flag a blank/invalid email only on opted-in contacts, so the
              // two flags don't stack (not-opted already won't be emailed).
              const invalidEmail = !notOpted && !hasValidEmail(contact.primary_email);
              return (
              <div key={contact.id} className="flex items-center justify-between px-3 py-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate flex items-center gap-1.5">
                    {contact.first_name} {contact.last_name}
                    {notOpted && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0 leading-tight font-normal shrink-0">
                        Not opted in
                      </Badge>
                    )}
                    {invalidEmail && (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0 leading-tight font-normal shrink-0">
                        Invalid email
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {contact.primary_email || '(no email)'}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  onClick={() => onRemoveContact(contact.id)}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center text-center h-[520px] text-muted-foreground border rounded-lg border-dashed">
            <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">No recipients selected</p>
            <p className="text-xs">Search for contacts to add</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 2: Compose ─────────────────────────────────────────────────

interface StepComposeProps {
  composeSubject: string;
  composeBody: string;
  composeSenderId: string;
  safetyValidationPassed: boolean;
  selectedContacts: Contact[];
  onSubjectChange: (v: string) => void;
  onBodyChange: (v: string) => void;
  onSenderChange: (v: string) => void;
}

function StepCompose({
  composeSubject,
  composeBody,
  composeSenderId,
  safetyValidationPassed,
  selectedContacts,
  onSubjectChange,
  onBodyChange,
  onSenderChange,
}: StepComposeProps) {
  return (
    <div className="space-y-4">
      <SequenceEmailComposer
        defaultSubject={composeSubject}
        defaultBody={composeBody}
        defaultSenderId={composeSenderId}
        onSubjectChange={onSubjectChange}
        onBodyChange={onBodyChange}
        onSenderChange={onSenderChange}
        onSave={() => {}} // No-op — wizard footer handles actions
        hideActions={true}
      />

      {/* Safety status indicator */}
      <div className="flex items-center gap-2 text-sm px-1">
        {selectedContacts.length === 0 ? (
          <>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">Select contacts to run safety checks</span>
          </>
        ) : safetyValidationPassed ? (
          <>
            <CheckCircle2 className="h-4 w-4 text-green-600" />
            <span className="text-green-600">Safety checks passed</span>
          </>
        ) : (
          <>
            <Loader2 className="h-4 w-4 text-muted-foreground animate-spin" />
            <span className="text-muted-foreground">Running safety checks...</span>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Step 3: Review (client-side mail-merge preview) ─────────────────

interface StepReviewPreviewProps {
  subject: string;
  body: string;
  contacts: Contact[];
  senderId: string;
  // Per-recipient inline edits, keyed by contact.id. Owned by the parent wizard
  // so they survive step navigation and are threaded into campaign creation.
  overrides: Map<string, { subject: string; body: string }>;
  onOverrideSave: (contactId: string, value: { subject: string; body: string }) => void;
  onOverrideReset: (contactId: string) => void;
}

function toMergeRecipient(c: Contact) {
  return {
    first_name: c.first_name,
    last_name: c.last_name,
    company: c.company_name ?? null,
    email: c.primary_email,
    occupation: c.job_title ?? null,
    phone: c.primary_phone,
  };
}

// TipTap blank lines are stored as <p></p> / <p><br></p>. Browsers (and most
// mail clients) collapse those to zero height, so the spacing the user added
// in compose visually disappears. Fill them with &nbsp; so a blank line has
// the height of one line — what gets sent already preserves the structure;
// this just makes the preview match what the recipient sees.
function fillBlankLines(html: string): string {
  return html
    .replace(/<p>\s*<\/p>/g, '<p>&nbsp;</p>')
    .replace(/<p>\s*<br\s*\/?>\s*<\/p>/g, '<p>&nbsp;</p>');
}

function StepReviewPreview({ subject, body, contacts, senderId, overrides, onOverrideSave, onOverrideReset }: StepReviewPreviewProps) {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | undefined>(contacts[0]?.id);
  const selected = contacts.find(c => c.id === selectedId) ?? contacts[0];

  // Inline edit mode for the currently-selected recipient. `editing` holds the
  // working subject/body while open; null means "showing the read-only preview".
  // Reset whenever the selected recipient changes so edits never bleed across
  // recipients.
  const [editing, setEditing] = useState<{ subject: string; body: string } | null>(null);
  useEffect(() => {
    setEditing(null);
  }, [selectedId]);

  // Resolve the sender's signature + physical mailing address the same way the
  // queue processor does (per-sender first, then workspace fallback). This makes
  // the preview reflect what actually gets appended to the recipient's email.
  const [senderExtras, setSenderExtras] = useState<{ signature: string; address: string }>({
    signature: '',
    address: '',
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!senderId) {
        if (!cancelled) setSenderExtras({ signature: '', address: '' });
        return;
      }
      const { data: prov } = await supabase
        .from('email_providers')
        .select('signature, physical_address, workspace_id')
        .eq('id', senderId)
        .maybeSingle();
      let address = (prov?.physical_address || '').trim();
      if (!address && prov?.workspace_id) {
        const { data: ws } = await supabase
          .from('workspace_settings')
          .select('compliance_physical_address')
          .eq('workspace_id', prov.workspace_id)
          .maybeSingle();
        address = (ws?.compliance_physical_address || '').trim();
      }
      if (!cancelled) {
        setSenderExtras({
          signature: (prov?.signature || '').trim(),
          address,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [senderId]);

  if (!selected) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
        <p className="text-sm">No recipients to preview</p>
      </div>
    );
  }

  const merge = toMergeRecipient(selected);
  const override = overrides.get(selected.id);
  const isCustomized = !!override;
  // Raw merged values (no fillBlankLines / signature / footer) — what the editor
  // seeds from and what gets stored as the override. The override, when present,
  // is the user's already-edited final text and wins over the template render.
  const baseSubject = override ? override.subject : renderCampaignTemplate(subject, merge);
  const baseBody = override ? override.body : renderCampaignTemplate(body, merge);
  const renderedSubject = baseSubject;
  const renderedBody = fillBlankLines(baseBody);

  const startEditing = () => setEditing({ subject: baseSubject, body: baseBody });
  const saveEditing = () => {
    if (!editing) return;
    // A per-recipient email can't be empty — this content is sent to a real
    // person. Mirror the campaign-level emptiness gate (strip tags/&nbsp; for the
    // body, since TipTap emits '<p></p>' for a cleared editor). Without this, a
    // blanked override defeats the scheduler's fallback and ships a near-empty
    // email. Cancel keeps the template instead.
    const subjectOk = editing.subject.trim() !== '';
    const bodyOk = editing.body.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
    if (!subjectOk || !bodyOk) {
      const missing = !subjectOk && !bodyOk ? 'subject and body' : !subjectOk ? 'subject' : 'body';
      toast({
        title: `Email ${missing} can't be empty`,
        description: 'Add content for this recipient, or Cancel to keep the campaign template.',
        variant: 'destructive',
      });
      return;
    }
    onOverrideSave(selected.id, { subject: editing.subject, body: editing.body });
    setEditing(null);
  };
  const resetToTemplate = () => {
    onOverrideReset(selected.id);
    setEditing(null);
  };

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Preview personalized per recipient — {contacts.length} recipient{contacts.length !== 1 ? 's' : ''}. Pick one to see exactly what they'll receive. Use <span className="font-medium">Edit</span> to tweak an individual email (e.g. a name or a one-off note); other recipients are unaffected.
      </p>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Recipient list */}
        <div className="lg:col-span-1 max-h-[420px] overflow-y-auto border rounded-lg divide-y">
          {contacts.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setSelectedId(c.id)}
              className={`w-full text-left px-3 py-2 hover:bg-muted ${c.id === selected.id ? 'bg-accent' : ''}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium truncate">{c.first_name} {c.last_name}</div>
                {overrides.has(c.id) && (
                  <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0">Customized</Badge>
                )}
              </div>
              <div className="text-xs text-muted-foreground truncate">{c.primary_email}</div>
            </button>
          ))}
        </div>

        {/* Rendered email */}
        <div className="lg:col-span-2 border rounded-lg p-4 space-y-3">
          {/* Header: recipient + per-recipient edit controls */}
          <div className="flex items-start justify-between gap-2">
            <div className="text-xs text-muted-foreground">
              To: <span className="text-foreground">{selected.primary_email || '(no email)'}</span>
              {isCustomized && !editing && (
                <Badge variant="secondary" className="ml-2 text-[10px] px-1.5 py-0">Customized</Badge>
              )}
            </div>
            {!editing && (
              <div className="flex items-center gap-1 shrink-0">
                {isCustomized && (
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={resetToTemplate}>
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />
                    Reset to template
                  </Button>
                )}
                <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={startEditing}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Edit
                </Button>
              </div>
            )}
          </div>

          {editing ? (
            /* ── Inline edit mode for this one recipient ─────────────── */
            <div className="space-y-3">
              <SequenceEmailComposer
                defaultSubject={editing.subject}
                defaultBody={editing.body}
                hideSender
                hideActions
                onSubjectChange={(s) => setEditing((e) => (e ? { ...e, subject: s } : e))}
                onBodyChange={(html) => setEditing((e) => (e ? { ...e, body: html } : e))}
                onSave={() => {}}
              />
              <p className="text-[11px] text-muted-foreground">
                Editing only this recipient. The sender signature and the CAN-SPAM footer are still added automatically at send time (not shown here).
              </p>
              <div className="flex items-center justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>Cancel</Button>
                <Button size="sm" onClick={saveEditing}>Save changes</Button>
              </div>
            </div>
          ) : (
            /* ── Read-only preview ───────────────────────────────────── */
            <>
              <div className="text-sm font-semibold border-b pb-2">
                {renderedSubject || <span className="text-muted-foreground font-normal">(empty subject)</span>}
              </div>
              <div
                className="text-sm prose prose-sm max-w-none dark:prose-invert"
                dangerouslySetInnerHTML={{ __html: renderedBody }}
              />
              {/* Sender signature — appended right after the body */}
              {senderExtras.signature && (
                <div
                  className="text-sm prose prose-sm max-w-none dark:prose-invert"
                  dangerouslySetInnerHTML={{ __html: senderExtras.signature }}
                />
              )}
              {/* Unified compliance footer — the physical mailing address (each
                  stored line on its own line) sits above the unsubscribe link
                  inside one bordered block, matching what the recipient sees. */}
              <div className="pt-2 flex items-start gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="text-muted-foreground hover:text-foreground"
                        aria-label="About the compliance footer"
                      >
                        <HelpCircle className="h-3.5 w-3.5 mt-1" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-xs text-xs">
                      Added to every campaign send for CAN-SPAM compliance. The mailing address and a per-recipient unsubscribe link are required by law; the unsubscribe URL is generated at send time.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <div
                  className="text-sm opacity-90 flex-1"
                  dangerouslySetInnerHTML={{ __html: buildComplianceFooterPreviewHtml(senderExtras.address) }}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Step 4: Schedule (pace + mode + estimate) ───────────────────────

interface StepScheduleProps {
  recipientCount: number;
  sendIntervalMinutes: number;
  onIntervalChange: (v: number) => void;
  schedulingMode: SchedulingMode;
  onSchedulingModeChange: (v: SchedulingMode) => void;
  existingPendingCount: number;
  safetyValidationPassed: boolean;
  senderDailyLimit: number;
}

function paceTier(interval: number): { label: string; badge: string; className: string } {
  if (interval < 15) return { label: 'Fastest', badge: 'Higher deliverability risk', className: 'text-amber-600' };
  if (interval === 15) return { label: 'Default', badge: 'Safe', className: 'text-emerald-600' };
  if (interval < 45) return { label: 'Brisk', badge: 'Safer', className: 'text-green-600' };
  return { label: 'Gentle', badge: 'Super safe', className: 'text-green-700' };
}

// First-order estimate. Accounts for both the per-send spacing and the
// selected sender's per-account daily cap. Deliberately approximate — labeled
// as an estimate — since the processor reschedules around cooldowns/retries.
function estimateCompletion(recipientCount: number, interval: number, dailyLimit: number): { date: Date; days: number } {
  const perDay = Math.max(1, Math.min(dailyLimit, Math.floor((24 * 60) / interval)));
  const days = Math.max(1, Math.ceil(recipientCount / perDay));
  const lastDayCount = recipientCount - (days - 1) * perDay;
  const lastDayOffsetMin = Math.max(0, lastDayCount - 1) * interval;
  const date = new Date(Date.now() + (days - 1) * 24 * 60 * 60 * 1000 + lastDayOffsetMin * 60 * 1000);
  return { date, days };
}

function StepSchedule({
  recipientCount,
  sendIntervalMinutes,
  onIntervalChange,
  schedulingMode,
  onSchedulingModeChange,
  existingPendingCount,
  safetyValidationPassed,
  senderDailyLimit,
}: StepScheduleProps) {
  const tier = paceTier(sendIntervalMinutes);
  const { date: estDate, days } = estimateCompletion(recipientCount, sendIntervalMinutes, senderDailyLimit);

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* Send pace */}
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <h3 className="text-sm font-medium">Send pace</h3>
          <span className={`text-sm font-medium ${tier.className}`}>
            {tier.label} · {tier.badge}
          </span>
        </div>
        {/* Quick-pick presets — keep slider for fine-grained values in between. */}
        <div className="flex flex-wrap gap-2">
          {INTERVAL_PRESETS.map((preset) => {
            const isActive = sendIntervalMinutes === preset.value;
            return (
              <Button
                key={preset.value}
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onIntervalChange(preset.value)}
                className={cn(
                  isActive &&
                    'border-primary bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground',
                )}
              >
                {preset.label}
              </Button>
            );
          })}
        </div>
        <Slider
          value={[sendIntervalMinutes]}
          min={MIN_INTERVAL_MINUTES}
          max={MAX_INTERVAL_MINUTES}
          step={5}
          onValueChange={(v) => onIntervalChange(v[0])}
        />
        {/* safe ↔ warning gauge */}
        <div className="h-1.5 rounded-full bg-gradient-to-r from-amber-500 via-yellow-400 to-green-500" />
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>1 email / {MIN_INTERVAL_MINUTES} min (fastest)</span>
          <span>1 email / {MAX_INTERVAL_MINUTES} min</span>
        </div>
        <p className="text-sm text-muted-foreground">
          Currently <span className="font-medium text-foreground">1 email every {sendIntervalMinutes} minutes</span>.
          {' '}{MIN_INTERVAL_MINUTES} minutes is the fastest allowed per account.
          Slower is gentler on sender reputation — especially for similar/templated
          content. {DEFAULT_INTERVAL_MINUTES} minutes is the recommended default.
          30 minutes and 1 hour are even safer for warming up new senders or large
          batches.
        </p>
      </div>

      {/* Estimated completion */}
      <div className="rounded-lg border p-4 space-y-1">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Clock className="h-4 w-4 text-muted-foreground" />
          Est. completion ~ {format(estDate, 'PP · p')}
        </div>
        <p className="text-xs text-muted-foreground">
          {recipientCount} email{recipientCount !== 1 ? 's' : ''} · ~{sendIntervalMinutes} min apart · ~{days} day{days !== 1 ? 's' : ''}.
          This is an <span className="font-medium">estimate</span> — actual timing can shift later
          due to per-recipient cooldowns, daily limits ({senderDailyLimit}/account/day), and retries.
        </p>
      </div>

      {/* Ordering mode */}
      <div className="space-y-3">
        <div>
          <h3 className="text-sm font-medium">Queue ordering</h3>
          <p className="text-xs text-muted-foreground">
            {existingPendingCount > 0
              ? `How to slot this campaign among ${existingPendingCount} email${existingPendingCount !== 1 ? 's' : ''} already queued on this account.`
              : 'Only affects ordering when other campaigns are queued on this account — nothing else is queued right now.'}
          </p>
        </div>
        <RadioGroup
          value={schedulingMode}
          onValueChange={(v) => onSchedulingModeChange(v as SchedulingMode)}
          className="space-y-2"
        >
          <ModeOption value="after_existing" id="wiz-mode-after" title="After existing (default)" desc="Queue behind active campaigns on the same account" />
          <ModeOption value="prioritize" id="wiz-mode-prioritize" title="Prioritize" desc="Send this campaign first, shift others back" />
          <ModeOption value="balanced" id="wiz-mode-balanced" title="Balanced" desc="Interleave evenly with all active campaigns" />
        </RadioGroup>
      </div>

      {!safetyValidationPassed && recipientCount > 0 && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Waiting on safety checks before sending can be enabled…
        </div>
      )}
    </div>
  );
}

function ModeOption({ value, id, title, desc }: { value: string; id: string; title: string; desc: string }) {
  return (
    <Label htmlFor={id} className="flex items-start space-x-3 border rounded-lg p-3 hover:bg-muted/50 cursor-pointer font-normal">
      <RadioGroupItem value={value} id={id} className="mt-0.5" />
      <span className="text-sm flex-1">
        <span className="font-medium">{title}</span>
        <span className="block text-muted-foreground">{desc}</span>
      </span>
    </Label>
  );
}
